import { api } from '../axios.helper';
import {
  Config,
  ReportTableDataDto,
  IncomeStatementsTypes,
  StatementsFeeTypes,
  StatementsContributionTypes,
  StatementsPayTypes,
  IncomeStatementDto,
  ExpenseItemDto,
  IncomeItemDto,
} from '@ecdlink/core';
import {
  StatementsExpensesInput,
  StatementsIncomeInput,
  SubmitStatementModelInput,
} from '@/../../../packages/graphql/lib';
import { inputObjectToCamelCase } from '@/utils/object-utils';

class IncomeStatementsService {
  _accessToken: string;

  constructor(accessToken: string) {
    this._accessToken = accessToken;
  }

  async GetAllStatementsIncomeType(): Promise<IncomeStatementsTypes[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'GetAllStatementsIncomeType',
    });

    if (response.status !== 200) {
      throw new Error(
        'Get all statements income Failed - Server connection error'
      );
    }

    return response.data.data.GetAllStatementsIncomeType;
  }

  async GetAllStatementsFeeType(): Promise<StatementsFeeTypes[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'GetAllStatementsFeeType',
    });

    if (response.status !== 200) {
      throw new Error(
        'Get all statements fee types Failed - Server connection error'
      );
    }

    return response.data.data.GetAllStatementsFeeType;
  }

  async GetAllStatementsContributionType(): Promise<
    StatementsContributionTypes[]
  > {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'GetAllStatementsContributionType',
    });

    if (response.status !== 200) {
      throw new Error(
        'Get all statements contribution types Failed - Server connection error'
      );
    }

    return response.data.data.GetAllStatementsContributionType;
  }

  async GetAllStatementsPayType(): Promise<StatementsPayTypes[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'GetAllStatementsPayType',
    });

    if (response.status !== 200) {
      throw new Error(
        'Get all statements pay types Failed - Server connection error'
      );
    }

    return response.data.data.GetAllStatementsPayType;
  }

  async UpdateStatementsIncome(
    input: StatementsIncomeInput
  ): Promise<IncomeItemDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'updateIncome',
      variables: {
        input: inputObjectToCamelCase(input),
      },
    });

    if (response.status !== 200 || !!response.data.errors) {
      throw new Error(
        'Update income statement Failed - Server connection error'
      );
    }

    return response.data.data.updateIncome;
  }

  // Used to generate the PDF, can we refactor to fetch a link to the backend PDF,
  // or to use the income statement to create the pdf? Then it could work offline?
  async getMonthsIncomeExpensesReport(
    statementId: string
  ): Promise<ReportTableDataDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: `GetStatementsIncomeExpensesPDFData`,
      variables: {
        statementId,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Get all income statementsreports Failed - Server connection error'
      );
    }
    return response.data.data.statementsIncomeExpensesPDFData;
  }

  async submitStatement(
    input: SubmitStatementModelInput
  ): Promise<IncomeStatementDto | undefined> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);

    const response = await apiInstance.post<any>(``, {
      id: 'submitMonthlyStatement',
      variables: {
        input,
      },
    });

    if (response.status !== 200 || !!response.data.errors) {
      throw new Error(
        'Submit income statement Failed - Server connection error'
      );
    }

    return response.data.data.submitMonthlyStatement;
  }

  async getIncomeStatements(
    userId: string,
    startDate: Date,
    endDate: Date | undefined
  ): Promise<IncomeStatementDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: `incomeStatements`,
      variables: {
        userId,
        startDate,
        endDate,
      },
    });

    if (response.status !== 200 || !!response.data.errors) {
      throw new Error('Get income statements Failed - Server connection error');
    }

    return response.data.data.incomeStatements;
  }

  async getUnsubmittedIncomeItems(userId: string): Promise<IncomeItemDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: `unsubmittedIncomeItems`,
      variables: {
        userId,
      },
    });

    if (response.status !== 200 || !!response.data.errors) {
      throw new Error(
        'Get unsubmitted income items Failed - Server connection error'
      );
    }

    return response.data.data.unsubmittedIncomeItems;
  }

  async getUnsubmittedExpenseItems(userId: string): Promise<ExpenseItemDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: `unsubmittedExpenseItems`,
      variables: {
        userId,
      },
    });

    if (response.status !== 200 || !!response.data.errors) {
      throw new Error(
        'Get unsubmitted expense items Failed - Server connection error'
      );
    }

    return response.data.data.unsubmittedExpenseItems;
  }

  async GetAllStatementsExpensesType(): Promise<any[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'GetAllStatementsExpenseType',
    });

    if (response.status !== 200) {
      throw new Error(
        'Get all statements expenses Failed - Server connection error'
      );
    }

    return response.data.data.GetAllStatementsExpenseType;
  }

  async updateStatementsExpense(
    input: StatementsExpensesInput
  ): Promise<ExpenseItemDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'updateExpense',
      variables: {
        input: inputObjectToCamelCase(input),
      },
    });

    if (response.status !== 200 || !!response.data.errors) {
      throw new Error(
        'Update expense statement Failed - Server connection error'
      );
    }

    return response.data.data.updateExpense;
  }

  async deleteExpense(id: string): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'deleteExpense',
      variables: {
        id,
      },
    });

    if (response.status !== 200) {
      throw new Error('Delete expense failed - Server connection error');
    }

    return true;
  }

  async deleteIncome(id: string): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'deleteIncome',
      variables: {
        id,
      },
    });

    if (response.status !== 200) {
      throw new Error('Delete expense failed - Server connection error');
    }

    return true;
  }

  async updateUserContactStatusForStatement(
    statementId: string
  ): Promise<IncomeStatementDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { updateUserContactStatusForStatement: IncomeStatementDto };
    }>(``, {
      id: 'updateUserContactStatusForStatement',
      variables: {
        statementId,
      },
    });

    if (response.status !== 200) {
      throw new Error('Updating user contact failed - Server connection error');
    }

    return response.data.data.updateUserContactStatusForStatement;
  }
}

export default IncomeStatementsService;
